import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

interface TestSubmission {
  id: number;
  tester_name: string;
  phone_number: string;
  testing_date: string;
  testing_method: string;
  responses: Record<string, string>;
  completed_scenarios: number;
  total_scenarios: number;
  submitted_at: string;
}

interface TesterStats {
  tester_name: string;
  total_submissions: number;
  total_scenarios_tested: number;
  avg_per_submission: number;
}

interface ScenarioStats {
  scenario_id: number;
  test_count: number;
  grades: Record<string, number>;
  avg_score: number;
}

export default function TestingResults() {
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState<TestSubmission[]>([]);
  const [testerStats, setTesterStats] = useState<TesterStats[]>([]);
  const [scenarioStats, setScenarioStats] = useState<ScenarioStats[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTestingData();
  }, []);

  const fetchTestingData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching testing data...');
      
      // Fetch all submissions
      const submissionsRes = await fetch('/api/ai-testing/results');
      console.log('Submissions response status:', submissionsRes.status);
      if (!submissionsRes.ok) {
        throw new Error(`Failed to fetch submissions: ${submissionsRes.status}`);
      }
      const submissionsData = await submissionsRes.json();
      console.log('Submissions data:', submissionsData);
      setSubmissions(submissionsData);

      // Fetch tester stats
      const statsRes = await fetch('/api/ai-testing/tester-stats');
      console.log('Stats response status:', statsRes.status);
      if (!statsRes.ok) {
        throw new Error(`Failed to fetch stats: ${statsRes.status}`);
      }
      const statsData = await statsRes.json();
      console.log('Stats data:', statsData);
      setTesterStats(statsData);

      // Fetch scenario breakdown
      const scenarioRes = await fetch('/api/ai-testing/scenario-stats');
      console.log('Scenario response status:', scenarioRes.status);
      if (!scenarioRes.ok) {
        throw new Error(`Failed to fetch scenario stats: ${scenarioRes.status}`);
      }
      const scenarioData = await scenarioRes.json();
      console.log('Scenario data:', scenarioData);
      setScenarioStats(scenarioData);

      console.log('All data fetched successfully');
      setLoading(false);
    } catch (err) {
      console.error('Error fetching testing data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Tester Name', 'Phone Number', 'Date', 'Method', 'Scenarios Tested', 'Submitted At'];
    const rows = submissions.map(sub => [
      sub.tester_name,
      sub.phone_number || 'N/A',
      sub.testing_date,
      sub.testing_method,
      `${sub.completed_scenarios}/${sub.total_scenarios}`,
      new Date(sub.submitted_at).toLocaleString()
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `testing-results-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getGradeColor = (grade: string) => {
    const colors: Record<string, string> = {
      'A+': '#4caf50',
      'A': '#8bc34a',
      'B': '#cddc39',
      'C': '#ffc107',
      'D': '#ff9800',
      'F': '#f44336',
    };
    return colors[grade] || '#9e9e9e';
  };

  const calculateOverallStats = () => {
    const totalSubmissions = submissions.length;
    const uniqueTesters = new Set(submissions.map(s => s.tester_name)).size;
    const totalScenariosCompleted = submissions.reduce((sum, s) => sum + (s.completed_scenarios || 0), 0);
    
    // Calculate grade distribution
    const allGrades: string[] = [];
    submissions.forEach(sub => {
      // Check if responses exists and is an object
      if (sub.responses && typeof sub.responses === 'object') {
        Object.entries(sub.responses).forEach(([key, value]) => {
          if (key.endsWith('-grade') && value && value !== 'N/A') {
            allGrades.push(value);
          }
        });
      }
    });

    const gradeDistribution: Record<string, number> = {};
    allGrades.forEach(grade => {
      gradeDistribution[grade] = (gradeDistribution[grade] || 0) + 1;
    });

    return { totalSubmissions, uniqueTesters, totalScenariosCompleted, gradeDistribution };
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading testing results...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Error Loading Data</Typography>
          <Typography>{error}</Typography>
          <Button variant="contained" onClick={fetchTestingData} sx={{ mt: 2 }}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  // Handle empty data
  if (!submissions || submissions.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 4 }}>
          AI Testing Results Dashboard
        </Typography>
        <Alert severity="info">
          <Typography variant="h6">No Test Results Yet</Typography>
          <Typography>
            No testing submissions have been recorded yet. Once testers start submitting results, they will appear here.
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Testing Form: <a href="/testing" style={{ color: '#1976d2' }}>https://ispguru.ai/testing</a>
          </Typography>
        </Alert>
      </Container>
    );
  }

  const stats = calculateOverallStats();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          AI Testing Results Dashboard
        </Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={exportToCSV}
          sx={{ bgcolor: '#1976d2' }}
        >
          Export CSV
        </Button>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon sx={{ mr: 1, color: '#1976d2' }} />
                <Typography variant="h6">Testers</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                {stats.uniqueTesters || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active testers
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AssignmentIcon sx={{ mr: 1, color: '#9c27b0' }} />
                <Typography variant="h6">Submissions</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                {stats.totalSubmissions || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total test submissions
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon sx={{ mr: 1, color: '#4caf50' }} />
                <Typography variant="h6">Scenarios</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                {stats.totalScenariosCompleted || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total scenarios tested
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ mr: 1, color: '#ff9800' }} />
                <Typography variant="h6">Avg Grade</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                {stats.gradeDistribution && Object.keys(stats.gradeDistribution).length > 0
                  ? Object.entries(stats.gradeDistribution).sort((a, b) => b[1] - a[1])[0][0]
                  : 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Most common grade
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Grade Distribution */}
      {stats.gradeDistribution && Object.keys(stats.gradeDistribution).length > 0 && (
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
          Grade Distribution
        </Typography>
        <Box sx={{ mt: 2 }}>
          {Object.entries(stats.gradeDistribution)
            .sort((a, b) => {
              const order = ['A+', 'A', 'B', 'C', 'D', 'F'];
              return order.indexOf(a[0]) - order.indexOf(b[0]);
            })
            .map(([grade, count]) => {
              const total = Object.values(stats.gradeDistribution).reduce((a, b) => a + b, 0);
              const percentage = (count / total) * 100;
              return (
                <Box key={grade} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: getGradeColor(grade) }}>
                      {grade}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {count} tests ({percentage.toFixed(1)}%)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: getGradeColor(grade),
                      },
                    }}
                  />
                </Box>
                );
              })}
        </Box>
      </Paper>
      )}

      {/* Tester Statistics */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
          Tester Statistics
        </Typography>
        {testerStats && testerStats.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tester Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Submissions</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Scenarios Tested</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Avg per Submission</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testerStats.map((tester) => (
                  <TableRow key={tester.tester_name} hover>
                    <TableCell>{tester.tester_name}</TableCell>
                    <TableCell>{tester.total_submissions}</TableCell>
                    <TableCell>{tester.total_scenarios_tested}</TableCell>
                    <TableCell>{Number(tester.avg_per_submission).toFixed(1)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography color="text.secondary">No tester statistics available yet.</Typography>
        )}
      </Paper>

      {/* Recent Submissions */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
          Recent Submissions
        </Typography>
        {submissions.slice(0, 10).map((submission) => (
          <Accordion key={submission.id} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                <Typography sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                  {submission.tester_name}
                </Typography>
                <Chip
                  label={submission.testing_method}
                  size="small"
                  color={submission.testing_method === 'voice' ? 'primary' : 'secondary'}
                />
                <Typography variant="body2" color="text.secondary">
                  {submission.completed_scenarios}/{submission.total_scenarios} scenarios
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(submission.submitted_at).toLocaleDateString()}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Phone:</strong> {submission.phone_number || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Submitted:</strong> {new Date(submission.submitted_at).toLocaleString()}
                </Typography>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Grades:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {submission.responses && typeof submission.responses === 'object' ? (
                    Object.entries(submission.responses)
                      .filter(([key]) => key.endsWith('-grade'))
                      .map(([key, grade]) => {
                        const scenarioId = key.replace('-grade', '');
                        return (
                          <Chip
                            key={key}
                            label={`Scenario ${scenarioId}: ${grade}`}
                            size="small"
                            sx={{
                              bgcolor: getGradeColor(grade),
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          />
                        );
                      })
                  ) : (
                    <Typography variant="body2" color="text.secondary">No grades available</Typography>
                  )}
                </Box>
                {submission.responses && typeof submission.responses === 'object' &&
                  Object.entries(submission.responses)
                    .filter(([key]) => key.endsWith('-notes'))
                    .some(([, notes]) => notes && notes.trim()) && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                      Notes:
                    </Typography>
                    {Object.entries(submission.responses)
                      .filter(([key, notes]) => key.endsWith('-notes') && notes && notes.trim())
                      .map(([key, notes]) => {
                        const scenarioId = key.replace('-notes', '');
                        return (
                          <Typography key={key} variant="body2" sx={{ mb: 1 }}>
                            <strong>Scenario {scenarioId}:</strong> {notes}
                          </Typography>
                        );
                      })}
                  </>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
    </Container>
  );
}

